import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Form, Message, Modal } from "semantic-ui-react";

import { getPurRecord, PurClientConfig, PurRecord } from "react-lib/apis/PUR/purApi";
import { getPurDocument, PurDocument } from "react-lib/apis/PUR/purDocumentsApi";
import { canVerifyPurDocuments, canVerifyPurDocumentType, performPurDocumentVerification, preparePurDocumentVerification, PurDocumentVerificationAccess, PurDocumentVerificationAttempt, PurDocumentVerificationError, reconcilePurDocumentVerification } from "react-lib/apis/PUR/purDocumentVerification";

import { PurVerificationStore, usePurVerificationStore } from "./purVerificationState";

interface Props {
  client: PurClientConfig;
  documentId: number;
  access?: PurDocumentVerificationAccess;
  store?: PurVerificationStore;
  onVerified: (document: PurDocument) => void;
}
interface Review { document: PurDocument; po: PurRecord | null }
type Result = "verified" | "alreadyVerified" | "unknown" | null;

const PurDocumentVerification: React.FC<Props> = ({ client, documentId, access, store, onVerified }) => {
  const intl = useIntl();
  const localStore = usePurVerificationStore();
  const sessions = store || localStore;
  const session = sessions.session(client, documentId);
  const [, setRevision] = useState(0);
  useEffect(() => sessions.subscribe(() => setRevision((revision) => revision + 1)), [sessions]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const submitting = session.inFlight;
  const [review, setReview] = useState<Review | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const [error, setError] = useState<string | null>(null);
  const [missingPermissions, setMissingPermissions] = useState<string[]>([]);
  const request = useRef(0);
  const busy = useRef(false);
  const permitted = canVerifyPurDocuments(client, access);

  useEffect(() => {
    request.current += 1;
    busy.current = false;
    setOpen(false); setReview(null); setAcknowledged(false); setResult(session.uncertain ? "unknown" : null);
    setLoading(false); setError(null); setMissingPermissions([]);
    return () => { request.current += 1; };
  }, [client, documentId, access, session]);

  const readReview = async () => {
    if (busy.current || session.inFlight) return;
    const token = ++request.current;
    busy.current = true;
    setOpen(true); setLoading(true); setAcknowledged(false); setError(null); setMissingPermissions([]);
    setReview(null); setResult(session.uncertain ? "unknown" : null);
    try {
      let document = await getPurDocument(client, documentId);
      if (request.current !== token) return;
      if (document.id !== documentId) throw new Error("Unexpected document ID");
      if (document.verified === true) {
        if (session.uncertain) {
          if (!session.attempt) throw new Error("No verification attempt available for reconciliation");
          const reconciled = await reconcilePurDocumentVerification(client, session.attempt);
          session.uncertain = false;
          session.attempt = null;
          sessions.changed();
          if (request.current !== token) return;
          document = reconciled.document;
          setResult("alreadyVerified"); onVerified(document);
          setReview({ document, po: reconciled.po });
          return;
        }
        setResult("alreadyVerified"); onVerified(document);
      }
      const po = document.poId === null ? null : await getPurRecord(client, "purchaseOrders", document.poId);
      if (request.current !== token) return;
      if (po && po.id !== document.poId) throw new Error("Unexpected PO ID");
      setReview({ document, po });
    } catch {
      if (request.current === token) setError("MEERP_VERIFY_READ_ERROR");
    } finally {
      if (request.current === token) { busy.current = false; setLoading(false); }
    }
  };

  const validReview = review !== null && review.document.verified === false
    && canVerifyPurDocumentType(review.document) && review.po !== null
    && review.document.vendorId !== null && review.document.vendorId === review.po.vendorId
    && review.document.projectId !== null && review.document.projectId === review.po.projectId
    && ["APPROVED", "PARTIALLY_RECEIVED", "TO_BILL", "TO_RECEIVE"].includes(review.po.status);

  const submit = async () => {
    if (busy.current || session.inFlight || session.uncertain || !permitted || !validReview || !review || !acknowledged) return;
    const token = ++request.current;
    busy.current = true;
    session.inFlight = true;
    sessions.changed(); setError(null);
    let prepared: PurDocumentVerificationAttempt | null = null;
    try {
      // Fresh document/PO/line and profile/project/vendor facts, plus only the
      // action permission that the resulting progress requires.
      prepared = await preparePurDocumentVerification(client, documentId, access);
      if (request.current !== token) return;
      if (prepared.document.verified === true) {
        setResult("alreadyVerified"); onVerified(prepared.document); return;
      }
      if (JSON.stringify(prepared.document) !== JSON.stringify(review.document) || JSON.stringify(prepared.po) !== JSON.stringify(review.po)) {
        setReview(null); setAcknowledged(false); setError("MEERP_VERIFY_CHANGED"); return;
      }
      session.attempt = prepared;
      // perform does one refreshed preflight, one POST and GET-only readback.
      session.uncertain = true;
      const outcome = await performPurDocumentVerification(client, prepared, access, () => request.current === token);
      // Update the retained operation even when its original detail has closed.
      session.uncertain = false;
      session.attempt = null;
      if (request.current !== token) return;
      setResult(outcome.verifiedByResponse ? "verified" : "alreadyVerified"); onVerified(outcome.document);
      setReview({ document: outcome.document, po: outcome.po });
    } catch (reason) {
      if (!session.uncertain || (reason instanceof PurDocumentVerificationError && reason.outcome === "blocked")) {
        session.uncertain = false;
        session.attempt = null;
        if (request.current !== token) return;
        setReview(null); setAcknowledged(false); setResult(null);
        setError(reason instanceof PurDocumentVerificationError ? reason.messageId : "MEERP_VERIFY_READ_ERROR");
        setMissingPermissions(reason instanceof PurDocumentVerificationError ? reason.permissionNames : []);
        return;
      }
      if (request.current !== token) return;
      const status = (reason as { response?: { status?: number } } | null)?.response?.status;
      setResult("unknown");
      setError(status === 401 || status === 403 ? "MEERP_VERIFY_FORBIDDEN" : "MEERP_VERIFY_UNKNOWN");
      // 400 can mean already verified OR another validation error. Inspect state.
      // perform already attempted the only safe GET-only reconciliation after
      // a POST failure. Keep this outcome unresolved; never issue a retry.
    } finally {
      session.inFlight = false;
      sessions.changed();
      if (request.current === token) busy.current = false;
    }
  };

  const close = () => {
    if (submitting) return;
    request.current += 1; busy.current = false; setLoading(false); setOpen(false);
  };
  const value = (v: string | number | null) => v === null || v === "" ? intl.formatMessage({ id: "MEERP_UNKNOWN" }) : String(v);
  return <>
    {!permitted && <p className="card-meerp__dependency">{intl.formatMessage({ id: "MEERP_VERIFY_ACCESS_PENDING" })}</p>}
    <Button type="button" disabled={submitting} onClick={readReview}>{intl.formatMessage({ id: "MEERP_VERIFY_REVIEW" })}</Button>
    <Modal open={open} onClose={close} closeOnEscape={!submitting} closeOnDimmerClick={!submitting} size="small" role="dialog" aria-modal="true" aria-labelledby="meerp-verify-title">
      <Modal.Header id="meerp-verify-title">{intl.formatMessage({ id: "MEERP_VERIFY_REVIEW" })}</Modal.Header>
      <Modal.Content>
        <p>{intl.formatMessage({ id: "MEERP_VERIFY_EFFECT" })}</p>
        {!permitted && <Message warning content={intl.formatMessage({ id: "MEERP_VERIFY_ACCESS_PENDING" })} />}
        {loading && <p role="status">{intl.formatMessage({ id: "MEERP_LOADING" })}</p>}
        {error && <Message negative role="alert" content={intl.formatMessage({ id: error })} />}
        {missingPermissions.length > 0 && <ul>{missingPermissions.map((name) => <li key={name}><code>{name}</code></li>)}</ul>}
        {result === "unknown" && !error && <Message warning role="alert" content={intl.formatMessage({ id: "MEERP_VERIFY_UNKNOWN" })} />}
        {(result === "verified" || result === "alreadyVerified") && <Message positive role="status" content={intl.formatMessage({ id: result === "verified" ? "MEERP_VERIFY_SUCCESS" : "MEERP_VERIFY_ALREADY" })} />}
        {review && <>
          <dl className="card-meerp__facts">{[
            ["MEERP_NUMBER", review.document.number], ["MEERP_VENDOR", review.document.vendorName],
            ["MEERP_TOTAL", review.document.grossAmount], ["MEERP_PRICE_VARIANCE", review.document.totalPriceVariance],
            ["MEERP_PO_ID", review.document.poId], ["MEERP_FIELD_PO_NUMBER", review.po?.number ?? null],
            ["MEERP_PERCENT_RECEIVED", review.po?.percentReceived ?? null], ["MEERP_PERCENT_BILLED", review.po?.percentBilled ?? null],
          ].map(([label, content]) => <div key={label}><dt>{intl.formatMessage({ id: String(label) })}</dt><dd>{value(content)}</dd></div>)}</dl>
          <p>{intl.formatMessage({ id: "MEERP_VERIFY_SERVER_PROGRESS" })}</p>
          {!validReview && review.document.verified !== true && <Message warning content={intl.formatMessage({ id: "MEERP_VERIFY_INELIGIBLE" })} />}
          {validReview && !result && <Form><Form.Checkbox label={intl.formatMessage({ id: "MEERP_VERIFY_ACKNOWLEDGE" })} checked={acknowledged} disabled={!permitted || submitting} onChange={(_, data) => setAcknowledged(data.checked === true)} /></Form>}
        </>}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" disabled={submitting} onClick={close}>{intl.formatMessage({ id: "MEERP_CLOSE_DETAIL" })}</Button>
        <Button type="button" disabled={loading || submitting} onClick={readReview}>{intl.formatMessage({ id: "MEERP_VERIFY_CHECK_STATUS" })}</Button>
        <Button type="button" primary loading={submitting} disabled={!permitted || !validReview || !acknowledged || loading || submitting || result !== null} onClick={submit}>{intl.formatMessage({ id: "MEERP_VERIFY_DOCUMENT" })}</Button>
      </Modal.Actions>
    </Modal>
  </>;
};

export default PurDocumentVerification;
